/**
 * FlowEditor - Editor visual de fluxo de onboarding (ReactFlow)
 *
 * Renderiza steps como nodes conectados por edges em um canvas interativo.
 * Suporta branches condicionais via multiple_choice, edicao inline via
 * painel de propriedades, e adicao de steps via sidebar ou botoes "+".
 *
 * Recebe steps (array plano) e emite onChange com array plano atualizado.
 * Internamente converte flat <-> tree para calcular layout automatico.
 */

import { useState, useCallback, useRef, useEffect, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  Handle,
  Position,
  BackgroundVariant,
  type NodeProps,
  type ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import FlowPreview from "./FlowPreview";
import { CalculatorIcon } from "./CalculatorIcon";

const CALC_SENTINEL = "__CALCULATOR_SVG__";

/**
 * Renderiza icone do step: SVG para loan_simulator, emoji pros demais.
 * Evita o problema de U+1F5A9 (pocket calculator) nao ter suporte RGI.
 */
export function StepEmoji({ emoji, size = 20 }: { emoji: string; size?: number }) {
  if (emoji === CALC_SENTINEL) return <CalculatorIcon size={size} />;
  return <span style={{ fontSize: size }}>{emoji}</span>;
}

// ============ Types ============

type OnboardingStepType =
  | "cpf_lookup" | "otp_whatsapp" | "cep_lookup" | "text_input"
  | "multiple_choice" | "multi_select" | "document_upload" | "selfie"
  | "video_recording" | "date_input" | "currency_input" | "number_input"
  | "info_screen" | "loan_simulator" | "create_password"
  /**
   * Pergunta com input condicional: o usuario escolhe uma opcao via
   * radio (ex: "E-mail" ou "Telefone") e cada opcao abre um input com
   * tipo/mascara especifico (text, email, phone, cpf, currency, etc).
   * Config relevante: `inputOptions` (lista) + `field`.
   */
  | "conditional_input"
  // Tipos que vem do DB em fluxos legados (ED) mas que o editor ainda nao modela
  // em profundidade: sao renderizados como "step generico" para nao crashar.
  | "phone_input" | "select" | "radio" | "file_upload"
  | "references" | "account_link" | "terms" | "penalty_warning"
  | "authorizations" | "pix_input" | "video_upload"
  | string;

/**
 * Tipos de input suportados pelo `conditional_input`. Cada um define qual
 * teclado/mascara o mobile aplica e qual validacao usar.
 */
export type ConditionalInputType =
  | "text"      // texto livre
  | "email"     // teclado e-mail, sem auto-cap
  | "phone"     // mascara (00) 00000-0000
  | "cpf"       // mascara 000.000.000-00
  | "number"    // numerico inteiro
  | "currency"  // R$ com formatacao
  | "date";     // DD/MM/AAAA

/**
 * Configuracao de um arquivo dentro de um step `document_upload`. O gestor
 * pode definir varios para coletar (ex: frente + verso da CNH no mesmo step).
 *   - `name`: identificador interno (slug do label)
 *   - `label`: texto mostrado pro cliente no slot
 *   - `required`: se true, o cliente nao consegue avancar sem enviar este
 */
export interface DocumentFieldConfig {
  name: string;
  label: string;
  required?: boolean;
}

export interface ConditionalInputOption {
  /** Identificador interno (vai pro responses como `field` selecionado). */
  value: string;
  /** Rotulo do radio button mostrado pro cliente. */
  label: string;
  /** Configuracao do input que aparece quando esta opcao e marcada. */
  input: {
    type: ConditionalInputType;
    /** Rotulo acima do input (ex: "E-mail vinculado"). Opcional. */
    label?: string;
    placeholder?: string;
  };
}

// Option pode vir como string ("Sim") ou como objeto ({label, value, icon?}).
type OnboardingOption = string | { label?: string; value?: string; icon?: string };

/**
 * Normaliza uma option para string exibivel/serializavel.
 * Regra: prefere .value (usado em branches), fallback para .label, por fim String(opt).
 */
function optionText(opt: OnboardingOption | undefined | null): string {
  if (opt == null) return "";
  if (typeof opt === "string") return opt;
  if (typeof opt === "object") return opt.value ?? opt.label ?? "";
  return String(opt);
}

interface OnboardingStepCondition {
  field?: string;
  equals?: string;
  not_equals?: string;
  in?: string[];
  not_in?: string[];
  not_empty?: boolean;
  and?: OnboardingStepCondition[];
  or?: OnboardingStepCondition[];
}

interface OnboardingStepConfig {
  label: string;
  field?: string;
  placeholder?: string;
  message?: string;
  options?: OnboardingOption[];
  accept?: string[];
  maxSizeMB?: number;
  minLength?: number;
  maxLength?: number;
  regex?: string;
  min?: number;
  max?: number;
  camera?: "front" | "back";
  maxDurationSeconds?: number;
  /** info_screen: exibe checkbox de aceite no mobile. */
  requireAccept?: boolean;
  /** info_screen: texto que aparece ao lado do checkbox. */
  acceptText?: string;
  /**
   * conditional_input: lista de opcoes radio + input que cada uma abre.
   * Ex: [{ value: "email", label: "E-mail", input: { type: "email", ... } },
   *      { value: "telefone", label: "Telefone", input: { type: "phone", ... } }]
   * Mobile renderiza os radios e troca o input ativo conforme selecao.
   */
  inputOptions?: ConditionalInputOption[];
  /**
   * document_upload: lista de arquivos que o cliente precisa enviar no mesmo
   * step. Ex: 1 step "CNH" com fields=[{name:"cnh_frente", label:"Frente"},
   * {name:"cnh_verso", label:"Verso"}]. Mobile renderiza N slots labelados
   * (1 por field). Quando ausente, cai no fallback single-slot.
   */
  fields?: DocumentFieldConfig[];
  /**
   * references: quantos contatos o cliente precisa cadastrar. Mobile lê
   * `config.quantity ?? 2` e renderiza N cards (nome + telefone cada).
   * Default da paleta = 2; gestor pode escolher 1-5.
   */
  quantity?: number;
  /**
   * references: se true (default), mobile valida cada telefone como WhatsApp
   * via API antes de avançar. Mantemos true por padrão — fluxo de cobrança
   * costuma precisar de WhatsApp válido pra acionar a referência.
   */
  validateWhatsApp?: boolean;
  /**
   * "Goto" — após concluir este step, pula direto para o step com este `id`
   * em vez de seguir pra o próximo na ordem. Valor especial `"__end__"`
   * encerra o onboarding (vai pra tela final).
   *
   * Usado pra evitar duplicar passos comuns no fim de branches diferentes.
   */
  goto?: string;
}

export interface OnboardingStep {
  /**
   * Identificador estável do step, sobrevive a reordenações. Gerado
   * automaticamente quando ausente (backfill no parse).
   */
  id?: string;
  order: number;
  type: OnboardingStepType;
  required: boolean;
  condition: OnboardingStepCondition | null;
  config: OnboardingStepConfig;
}

/**
 * Sentinela de goto que encerra o fluxo (vai pra tela de sucesso/saída).
 */
export const GOTO_END = "__end__";

let _stepIdCounter = 0;
function generateStepId(): string {
  return `s_${Date.now().toString(36)}_${(++_stepIdCounter).toString(36)}`;
}

/**
 * Plano usado pelo preview do simulador. Vem do wizard (lista de installments
 * configuradas em "Parcelas"). Quando o gestor tem 1+ planos, o preview pega o
 * primeiro pra mostrar uma simulacao real (em vez de placeholder R$ 0,00 / --%).
 * Mantemos a forma minima — qualquer subset de ChargePlanInstallment serve.
 */
export interface SimulatorPlanPreview {
  tipo: "diario" | "semanal" | "quinzenal" | "mensal" | "personalizado" | string;
  parcelas: number;
  /** Taxa em % (string igual ao schema da API, ex: "15.00"). */
  taxaPct: string;
  label?: string | null;
}

interface FlowEditorProps {
  steps: OnboardingStep[];
  onChange: (steps: OnboardingStep[]) => void;
  /**
   * Planos pra preview do simulador. Renderiza simulacao do 1o plano quando
   * houver — fora isso o preview mostra placeholder.
   */
  simulatorPlans?: SimulatorPlanPreview[];
  /** Valor minimo configurado no wizard — usado como exemplo na simulacao. */
  simulatorValorMinimo?: string;
}

// ============ FlowNode (tree representation) ============

interface FlowNode {
  id: string;
  step: OnboardingStep;
  branches?: {
    optionValue: string;
    nodes: FlowNode[];
  }[];
}

// ============ Palette item definitions ============

interface PaletteItem {
  type: OnboardingStepType;
  emoji: string;
  labelKey: string;
  categoryKey: string;
  defaults: Partial<OnboardingStepConfig> & { label: string };
  required?: boolean;
}

const PALETTE_ITEMS: PaletteItem[] = [
  // Identificacao
  {
    type: "cpf_lookup", emoji: "\u{1FAAA}", labelKey: "flow_editor.palette.cpf", categoryKey: "flow_editor.category.perguntas",
    defaults: { label: "Informe seu CPF", field: "cpf" }, required: true,
  },
  {
    type: "otp_whatsapp", emoji: "\u{1F4AC}", labelKey: "flow_editor.palette.whatsapp", categoryKey: "flow_editor.category.perguntas",
    defaults: { label: "Informe seu WhatsApp", field: "whatsapp" }, required: true,
  },
  // Localizacao
  {
    type: "cep_lookup", emoji: "\u{1F4CD}", labelKey: "flow_editor.palette.endereco", categoryKey: "flow_editor.category.localizacao",
    defaults: { label: "Onde voce mora?", field: "endereco" }, required: true,
  },
  // Documentos
  {
    type: "document_upload", emoji: "\u{1F4C4}", labelKey: "flow_editor.palette.documento", categoryKey: "flow_editor.category.documentos",
    defaults: {
      label: "Envie seu documento",
      accept: ["image/*", "application/pdf"],
      maxSizeMB: 10,
      // 1 arquivo por padrao; gestor pode adicionar (ex: frente + verso da CNH).
      fields: [{ name: "doc_1", label: "Documento", required: true }],
    },
  },
  {
    type: "selfie", emoji: "\u{1F4F8}", labelKey: "flow_editor.palette.selfie", categoryKey: "flow_editor.category.documentos",
    defaults: { label: "Tire uma selfie", camera: "front" }, required: true,
  },
  {
    type: "video_recording", emoji: "\u{1F3A5}", labelKey: "flow_editor.palette.video", categoryKey: "flow_editor.category.documentos",
    // Camera traseira e duracao maxima de 60s sao fixos no codigo — gestor
    // nao precisa escolher (e dificilmente sabe o que e melhor).
    defaults: { label: "Grave um video", camera: "back", maxDurationSeconds: 60 },
  },
  // Perguntas
  {
    type: "multiple_choice", emoji: "\u{1F518}", labelKey: "flow_editor.palette.multiple_choice", categoryKey: "flow_editor.category.perguntas",
    defaults: { label: "Selecione uma opcao", options: ["Opcao 1", "Opcao 2"] },
  },
  {
    type: "multi_select", emoji: "\u2611\uFE0F", labelKey: "flow_editor.palette.multi_select", categoryKey: "flow_editor.category.perguntas",
    defaults: { label: "Selecione as opcoes", options: ["Opcao 1", "Opcao 2"] },
  },
  {
    type: "text_input", emoji: "\u270F\uFE0F", labelKey: "flow_editor.palette.text_input", categoryKey: "flow_editor.category.perguntas",
    defaults: { label: "Responda a pergunta", placeholder: "Digite aqui..." },
  },
  {
    type: "date_input", emoji: "\u{1F4C5}", labelKey: "flow_editor.palette.date_input", categoryKey: "flow_editor.category.perguntas",
    defaults: { label: "Selecione uma data" },
  },
  {
    type: "currency_input", emoji: "\u{1F4B0}", labelKey: "flow_editor.palette.currency_input", categoryKey: "flow_editor.category.perguntas",
    defaults: { label: "Informe o valor", min: 0 },
  },
  {
    type: "number_input", emoji: "\u{1F522}", labelKey: "flow_editor.palette.number_input", categoryKey: "flow_editor.category.perguntas",
    defaults: { label: "Informe o numero" },
  },
  {
    type: "conditional_input", emoji: "\u{1F500}", labelKey: "flow_editor.palette.conditional_input", categoryKey: "flow_editor.category.perguntas",
    defaults: {
      label: "Pergunta com resposta condicional",
      field: "conditional",
      inputOptions: [
        { value: "opcao1", label: "Opcao 1", input: { type: "text", label: "Resposta", placeholder: "Digite aqui..." } },
        { value: "opcao2", label: "Opcao 2", input: { type: "text", label: "Resposta", placeholder: "Digite aqui..." } },
      ],
    },
    required: true,
  },
  // Compostos
  {
    type: "references", emoji: "\u{1F465}", labelKey: "flow_editor.palette.ref_pessoal", categoryKey: "flow_editor.category.compostos",
    // Mobile renderiza N cards (nome + telefone) e valida cada telefone como
    // WhatsApp. Gestor escolhe a quantidade no PropertiesPanel.
    defaults: { label: "Cadastre referencias pessoais", quantity: 2, validateWhatsApp: true },
    required: true,
  },
  {
    type: "text_input", emoji: "\u2709\uFE0F", labelKey: "flow_editor.palette.email_ou_telefone", categoryKey: "flow_editor.category.compostos",
    defaults: { label: "E-mail ou telefone de contato", message: "email_ou_telefone" },
  },
  {
    type: "text_input", emoji: "\u26A1", labelKey: "flow_editor.palette.chave_pix", categoryKey: "flow_editor.category.compostos",
    defaults: { label: "Informe sua chave PIX", message: "chave_pix", placeholder: "CPF, e-mail, telefone ou chave aleatoria" },
  },
  // Finalizacao
  {
    type: "info_screen", emoji: "\u2139\uFE0F", labelKey: "flow_editor.palette.info_screen", categoryKey: "flow_editor.category.finalizacao",
    defaults: { label: "Termos de uso", message: "Leia e aceite os termos para continuar." },
  },
  {
    type: "loan_simulator", emoji: "__CALCULATOR_SVG__", labelKey: "flow_editor.palette.loan_simulator", categoryKey: "flow_editor.category.finalizacao",
    defaults: { label: "Simule sua cobranca" },
  },
  {
    type: "create_password", emoji: "\u{1F510}", labelKey: "flow_editor.palette.create_password", categoryKey: "flow_editor.category.finalizacao",
    defaults: { label: "Crie sua senha de acesso", field: "senha", minLength: 6 }, required: true,
  },
];

const CATEGORY_KEYS = [
  "flow_editor.category.localizacao",
  "flow_editor.category.documentos",
  "flow_editor.category.perguntas",
  "flow_editor.category.compostos",
  "flow_editor.category.finalizacao",
];

// ============ Emoji map by step type ============

/**
 * Mapa de emoji por tipo de step \u2014 fonte unica da verdade para o icone que
 * aparece dentro do card no canvas. Mantenha em sincronia com PALETTE_ITEMS
 * (cada `type` deve ter o mesmo emoji aqui e na paleta).
 *
 * `selfie` e `video_recording` usam os mesmos icones que o app mobile mostra
 * nos botoes de captura (\uD83D\uDCF8 e \uD83C\uDFA5). Os demais nao tem icone proprio no mobile
 * (renderizam apenas formulario), entao usei icones semanticos coerentes.
 */
export const TYPE_EMOJI: Record<string, string> = {
  cpf_lookup: "\u{1FAAA}",
  otp_whatsapp: "\u{1F4AC}",
  cep_lookup: "\u{1F4CD}",
  text_input: "\u270F\uFE0F",
  multiple_choice: "\u{1F518}",
  multi_select: "\u2611\uFE0F",
  document_upload: "\u{1F4C4}",
  selfie: "\u{1F4F8}",
  video_recording: "\u{1F3A5}",
  date_input: "\u{1F4C5}",
  currency_input: "\u{1F4B0}",
  number_input: "\u{1F522}",
  info_screen: "\u2139\uFE0F",
  loan_simulator: "__CALCULATOR_SVG__",
  create_password: "\u{1F510}",
  references: "\u{1F465}",
  pix_input: "\u26A1",
  terms: "\u{1F4DC}",
  penalty_warning: "\u26A0\uFE0F",
  conditional_input: "\u{1F500}",
};

export const TYPE_LABEL_KEY: Record<string, string> = {
  cpf_lookup: "flow_editor.type.cpf",
  otp_whatsapp: "flow_editor.type.whatsapp",
  cep_lookup: "flow_editor.type.cep",
  text_input: "flow_editor.type.text",
  multiple_choice: "flow_editor.type.multiple_choice",
  multi_select: "flow_editor.type.multi_select",
  document_upload: "flow_editor.type.document",
  selfie: "flow_editor.type.selfie",
  video_recording: "flow_editor.type.video",
  date_input: "flow_editor.type.date",
  currency_input: "flow_editor.type.currency",
  number_input: "flow_editor.type.number",
  info_screen: "flow_editor.type.info",
  loan_simulator: "flow_editor.type.simulator",
  create_password: "flow_editor.type.password",
  conditional_input: "flow_editor.type.conditional_input",
  references: "flow_editor.type.references",
};

// ============ Layout constants ============

const NODE_WIDTH = 260;
const NODE_HEIGHT = 80;
/**
 * Estima altura do StepNode no canvas a partir do label e opções. Largura é
 * fixa (NODE_WIDTH) — cards têm a mesma largura mas alturas diferentes pra
 * comportar o texto inteiro sem truncar.
 *
 * O card tem: número (24px) + emoji (24px) + área flexível (label +
 * typeBadge) + badge REQ opcional (~40px). Com REQ visível sobra ~120px de
 * largura útil pra label em text-sm font-medium → ~17 chars/linha.
 * Sem REQ sobra ~160px → ~22 chars. Uso o pior caso pra evitar overlap.
 *
 * Cada linha extra adiciona ~22px (line-height snug ~1.4 * 14px + buffer).
 * Chips de opções wrap em rows de ~4 chips, cada row ~22px.
 */
function estimateNodeHeight(
  label: string | undefined,
  hasOptions: boolean,
  optionsCount: number = 0,
  hasGoto: boolean = false,
): number {
  const charsPerLine = 17;
  const text = label ?? "";
  const explicitLines = text.split(/\r?\n/).length;
  const wrappedLines = text
    .split(/\r?\n/)
    .reduce((acc, line) => acc + Math.max(1, Math.ceil(line.length / charsPerLine)), 0);
  const lines = Math.max(explicitLines, wrappedLines, 1);
  let height = NODE_HEIGHT + Math.max(0, lines - 1) * 22;
  if (hasOptions) {
    const optionRows = Math.max(1, Math.ceil(optionsCount / 4));
    height += 8 + optionRows * 22;
  }
  if (hasGoto) height += 24;
  return height;
}
/**
 * Espaço visual mínimo entre as bordas de duas branches irmãs. Usado por
 * `computeBranchWidth` pra empilhar branches lado a lado sem sobreposição
 * quando uma delas tem sub-escolhas que se expandem horizontalmente.
 */
const MIN_BRANCH_GAP = 40;
const START_X = 400;
const ADD_BUTTON_HEIGHT = 36;

/**
 * Calcula recursivamente a largura horizontal que uma lista de nodes precisa
 * ocupar pra caber sem invadir colunas vizinhas. Considera sub-branches em
 * qualquer profundidade — se um filho é multiple_choice e tem sub-branches,
 * sua largura efetiva é a soma das larguras delas + gaps.
 */
function computeBranchWidth(nodes: FlowNode[]): number {
  let maxWidth = NODE_WIDTH;
  for (const n of nodes) {
    if (n.branches && n.branches.length > 0) {
      let childTotal = 0;
      for (const b of n.branches) {
        childTotal += computeBranchWidth(b.nodes);
      }
      childTotal += Math.max(0, n.branches.length - 1) * MIN_BRANCH_GAP;
      if (childTotal > maxWidth) maxWidth = childTotal;
    }
  }
  return maxWidth;
}

// ============ Unique ID helper ============

let _idCounter = 0;
function uid(): string {
  return `fn_${Date.now()}_${++_idCounter}`;
}

// ============ Flat <-> Tree conversion ============

function parseFlowTree(steps: OnboardingStep[]): FlowNode[] {
  if (!steps || steps.length === 0) return [];

  const sorted = [...steps].sort((a, b) => a.order - b.order);
  const rootNodes: FlowNode[] = [];
  const branchOwners = new Map<string, FlowNode>();

  for (const rawStep of sorted) {
    // Backfill id quando vier vazio (dados legados sem identificador estável)
    const step: OnboardingStep = rawStep.id ? rawStep : { ...rawStep, id: generateStepId() };
    const cond = step.condition;

    if (cond && cond.field && cond.equals !== undefined) {
      const ownerNode = branchOwners.get(cond.field);
      if (ownerNode && ownerNode.branches) {
        const branch = ownerNode.branches.find((b) => b.optionValue === cond.equals);
        if (branch) {
          const node: FlowNode = { id: uid(), step: { ...step } };
          if (step.type === "multiple_choice" && step.config.field && step.config.options) {
            node.branches = step.config.options.map((opt) => ({
              optionValue: optionText(opt),
              nodes: [],
            }));
            branchOwners.set(step.config.field, node);
          }
          branch.nodes.push(node);
          continue;
        }
      }
    }

    const node: FlowNode = { id: uid(), step: { ...step, condition: null } };

    if (step.type === "multiple_choice" && step.config.field && step.config.options) {
      node.branches = step.config.options.map((opt) => ({
        optionValue: optionText(opt),
        nodes: [],
      }));
      branchOwners.set(step.config.field, node);
    }

    rootNodes.push(node);
  }

  return rootNodes;
}

function flattenFlowTree(nodes: FlowNode[], startOrder = 1, parentCondition: OnboardingStepCondition | null = null): OnboardingStep[] {
  const result: OnboardingStep[] = [];
  let order = startOrder;

  for (const node of nodes) {
    const step: OnboardingStep = {
      ...node.step,
      order,
      condition: parentCondition,
    };
    result.push(step);
    order++;

    if (node.branches) {
      for (const branch of node.branches) {
        const branchCondition: OnboardingStepCondition = {
          field: node.step.config.field,
          equals: branch.optionValue,
        };
        const branchSteps = flattenFlowTree(branch.nodes, order, branchCondition);
        result.push(...branchSteps);
        order += branchSteps.length;
      }
    }
  }

  return result;
}

// ============ Step factory ============

function createStepFromPalette(item: PaletteItem, existingFields: Set<string>): OnboardingStep {
  const base = item.defaults.field
    || (item.type === "text_input" && item.defaults.message ? item.defaults.message : item.type);
  let field = base;
  let counter = 1;
  while (existingFields.has(field)) {
    field = `${base}_${counter}`;
    counter++;
  }

  return {
    id: generateStepId(),
    order: 0,
    type: item.type,
    required: item.required ?? true,
    condition: null,
    config: {
      ...item.defaults,
      field,
    },
  };
}

function collectFields(nodes: FlowNode[]): Set<string> {
  const fields = new Set<string>();
  for (const node of nodes) {
    if (node.step.config.field) fields.add(node.step.config.field);
    if (node.branches) {
      for (const branch of node.branches) {
        const sub = collectFields(branch.nodes);
        sub.forEach((f) => fields.add(f));
      }
    }
  }
  return fields;
}

// ============ Auto-layout: tree -> ReactFlow nodes + edges ============

interface LayoutResult {
  rfNodes: Node[];
  rfEdges: Edge[];
  totalHeight: number;
}

function computeStepNumbers(flowNodes: FlowNode[]): Map<string, number> {
  const map = new Map<string, number>();
  let counter = 0;
  function walk(list: FlowNode[]) {
    for (const n of list) {
      counter++;
      map.set(n.id, counter);
      if (n.branches) {
        for (const b of n.branches) {
          walk(b.nodes);
        }
      }
    }
  }
  walk(flowNodes);
  return map;
}

/**
 * Constrói mapas auxiliares percorrendo a árvore antes do layout:
 * - stepIdToFlowId: step.id estável → FlowNode.id (id usado pelo ReactFlow)
 * - stepIdToStepNumber: step.id → posição visual (1, 2, 3…) p/ exibir nos badges
 */
function buildStepIdMaps(flowNodes: FlowNode[], stepNumbers: Map<string, number>) {
  const stepIdToFlowId = new Map<string, string>();
  const stepIdToStepNumber = new Map<string, number>();
  function walk(list: FlowNode[]) {
    for (const n of list) {
      if (n.step.id) {
        stepIdToFlowId.set(n.step.id, n.id);
        const num = stepNumbers.get(n.id);
        if (num !== undefined) stepIdToStepNumber.set(n.step.id, num);
      }
      if (n.branches) {
        for (const b of n.branches) walk(b.nodes);
      }
    }
  }
  walk(flowNodes);
  return { stepIdToFlowId, stepIdToStepNumber };
}

function autoLayout(
  flowNodes: FlowNode[],
  stepNumbers: Map<string, number>,
  t: (key: string, opts?: any) => string,
): LayoutResult {
  const rfNodes: Node[] = [];
  const rfEdges: Edge[] = [];

  const { stepIdToFlowId, stepIdToStepNumber } = buildStepIdMaps(flowNodes, stepNumbers);

  let currentY = 0;
  const centerX = START_X;

  // START node
  const startId = "__start__";
  rfNodes.push({
    id: startId,
    type: "startNode",
    position: { x: centerX - 30, y: currentY },
    data: {},
    draggable: false,
    selectable: false,
  });
  currentY += 60;

  // Add button before first node
  if (flowNodes.length > 0) {
    const addId = `__add_root_0__`;
    rfNodes.push({
      id: addId,
      type: "addButtonNode",
      position: { x: centerX - 15, y: currentY },
      data: { insertTarget: "root", insertIndex: 0 },
      draggable: false,
      selectable: false,
    });
    rfEdges.push({
      id: `e-start-add0`,
      source: startId,
      target: addId,
      type: "smoothstep",
      animated: true,
      style: { stroke: "#9ca3af", strokeWidth: 2 },
    });
    currentY += ADD_BUTTON_HEIGHT + 10;
  }

  let prevNodeId = flowNodes.length > 0 ? `__add_root_0__` : startId;

  for (let i = 0; i < flowNodes.length; i++) {
    const flowNode = flowNodes[i];
    const stepNum = stepNumbers.get(flowNode.id) || 0;
    const emoji = TYPE_EMOJI[flowNode.step.type] || "\u2753";
    const typeLabelKey = TYPE_LABEL_KEY[flowNode.step.type];
    const typeLabel = typeLabelKey ? t(typeLabelKey) : flowNode.step.type;
    const hasOptions = (flowNode.step.type === "multiple_choice" || flowNode.step.type === "multi_select")
      && flowNode.step.config.options && flowNode.step.config.options.length > 0;
    const effectiveHeight = estimateNodeHeight(
      flowNode.step.config.label,
      Boolean(hasOptions),
      flowNode.step.config.options?.length ?? 0,
      Boolean(flowNode.step.config.goto),
    );

    const nodeId = flowNode.id;
    const gotoId = flowNode.step.config.goto;
    const gotoInfo = gotoId
      ? gotoId === GOTO_END
        ? { kind: "end" as const }
        : { kind: "step" as const, targetNumber: stepIdToStepNumber.get(gotoId) ?? 0 }
      : null;
    rfNodes.push({
      id: nodeId,
      type: "stepNode",
      position: { x: centerX - NODE_WIDTH / 2, y: currentY },
      data: {
        flowNodeId: flowNode.id,
        emoji,
        label: flowNode.step.config.label,
        typeBadge: typeLabel,
        required: flowNode.step.required,
        options: hasOptions ? flowNode.step.config.options : null,
        stepNumber: stepNum,
        gotoInfo,
      },
      draggable: false,
    });

    rfEdges.push({
      id: `e-${prevNodeId}-${nodeId}`,
      source: prevNodeId,
      target: nodeId,
      type: "smoothstep",
      animated: true,
      style: { stroke: "#9ca3af", strokeWidth: 2 },
    });

    currentY += effectiveHeight + 10;

    // Handle branches
    if (flowNode.branches && flowNode.branches.length > 0) {
      const branchResult = layoutBranches(
        flowNode,
        centerX,
        currentY,
        stepNumbers,
        t,
        stepIdToFlowId,
        stepIdToStepNumber,
      );
      rfNodes.push(...branchResult.nodes);
      rfEdges.push(...branchResult.edges);
      currentY += branchResult.height;
    }

    // Add button after this node (between nodes or at end)
    const addIdx = i + 1;
    const addId = `__add_root_${addIdx}__`;
    rfNodes.push({
      id: addId,
      type: "addButtonNode",
      position: { x: centerX - 15, y: currentY },
      data: { insertTarget: "root", insertIndex: addIdx },
      draggable: false,
      selectable: false,
    });

    // Edge from step node (or branch merge) to add button
    const lastSourceForEdge = flowNode.branches && flowNode.branches.length > 0
      ? `__merge_${flowNode.id}__`
      : nodeId;
    rfEdges.push({
      id: `e-${lastSourceForEdge}-${addId}`,
      source: lastSourceForEdge,
      target: addId,
      type: "smoothstep",
      animated: true,
      style: { stroke: "#9ca3af", strokeWidth: 2 },
    });

    currentY += ADD_BUTTON_HEIGHT + 10;
    prevNodeId = addId;
  }

  // END node
  const endId = "__end__";
  rfNodes.push({
    id: endId,
    type: "endNode",
    position: { x: centerX - 30, y: currentY },
    data: {},
    draggable: false,
    selectable: false,
  });

  rfEdges.push({
    id: `e-${prevNodeId}-end`,
    source: prevNodeId,
    target: endId,
    type: "smoothstep",
    animated: true,
    style: { stroke: "#9ca3af", strokeWidth: 2 },
  });

  currentY += 60;

  // Overlay edges para gotos: percorre TODOS os steps (root + branches) e,
  // pra cada um que tem config.goto, desenha uma seta laranja tracejada
  // ligando-o ao destino. Roda no fim pra ter certeza que ambos os
  // endpoints já existem em rfNodes.
  function addGotoEdges(list: FlowNode[]) {
    for (const n of list) {
      const target = n.step.config.goto;
      if (target) {
        const targetRfId = target === GOTO_END ? endId : stepIdToFlowId.get(target);
        if (targetRfId) {
          rfEdges.push({
            id: `e-goto-${n.id}-${targetRfId}`,
            source: n.id,
            target: targetRfId,
            type: "smoothstep",
            animated: true,
            label: target === GOTO_END
              ? t("flow_editor.goto_badge_end")
              : t("flow_editor.goto_badge", { n: stepIdToStepNumber.get(target) ?? "?" }),
            labelStyle: { fill: "#ea580c", fontWeight: 600, fontSize: 10 },
            labelBgStyle: { fill: "#fff7ed" },
            labelBgPadding: [4, 6],
            labelBgBorderRadius: 6,
            style: { stroke: "#ea580c", strokeWidth: 2, strokeDasharray: "6,4" },
            zIndex: 1000,
          });
        }
      }
      if (n.branches) {
        for (const b of n.branches) addGotoEdges(b.nodes);
      }
    }
  }
  addGotoEdges(flowNodes);

  return { rfNodes, rfEdges, totalHeight: currentY };
}

function layoutBranches(
  parentFlowNode: FlowNode,
  parentCenterX: number,
  startY: number,
  stepNumbers: Map<string, number>,
  t: (key: string, opts?: any) => string,
  stepIdToFlowId: Map<string, string>,
  stepIdToStepNumber: Map<string, number>,
): { nodes: Node[]; edges: Edge[]; height: number } {
  const branches = parentFlowNode.branches || [];
  if (branches.length === 0) return { nodes: [], edges: [], height: 0 };

  const rfNodes: Node[] = [];
  const rfEdges: Edge[] = [];

  const numBranches = branches.length;

  // Calcula largura individual de cada branch (cobrindo sub-escolhas
  // aninhadas) e posiciona as colunas lado a lado. Isso evita que branches
  // profundas invadam a coluna vizinha quando crescem na largura.
  const branchWidths = branches.map((b) => computeBranchWidth(b.nodes));
  const totalWidth =
    branchWidths.reduce((a, b) => a + b, 0) + Math.max(0, numBranches - 1) * MIN_BRANCH_GAP;
  let cursorX = parentCenterX - totalWidth / 2;

  let labelY = startY + 20;
  let maxBranchHeight = 0;

  const branchColumns: { x: number; firstId: string | null; lastId: string | null; height: number; branchLabel: string }[] = [];

  for (let bIdx = 0; bIdx < branches.length; bIdx++) {
    const branch = branches[bIdx];
    const colWidth = branchWidths[bIdx];
    const colCenterX = cursorX + colWidth / 2;
    cursorX += colWidth + MIN_BRANCH_GAP;

    // Branch label node
    const labelId = `__blabel_${parentFlowNode.id}_${bIdx}__`;
    rfNodes.push({
      id: labelId,
      type: "branchLabelNode",
      position: { x: colCenterX - 60, y: labelY },
      data: { label: branch.optionValue },
      draggable: false,
      selectable: false,
    });

    // Edge from parent to branch label
    rfEdges.push({
      id: `e-${parentFlowNode.id}-${labelId}`,
      source: parentFlowNode.id,
      target: labelId,
      type: "smoothstep",
      style: { stroke: "#6b7280", strokeWidth: 1.5, strokeDasharray: "5,5" },
    });

    let colY = labelY + 40;
    let prevId = labelId;
    let firstNodeId: string | null = null;
    let lastNodeId: string | null = labelId;

    // Add button at start of branch
    const addTopId = `__add_branch_${parentFlowNode.id}_${bIdx}_0__`;
    rfNodes.push({
      id: addTopId,
      type: "addButtonNode",
      position: { x: colCenterX - 15, y: colY },
      data: {
        insertTarget: "branch",
        parentNodeId: parentFlowNode.id,
        branchOption: branch.optionValue,
        insertIndex: 0,
      },
      draggable: false,
      selectable: false,
    });
    rfEdges.push({
      id: `e-${prevId}-${addTopId}`,
      source: prevId,
      target: addTopId,
      type: "smoothstep",
      style: { stroke: "#6b7280", strokeWidth: 1.5 },
    });
    colY += ADD_BUTTON_HEIGHT + 10;
    prevId = addTopId;

    for (let nIdx = 0; nIdx < branch.nodes.length; nIdx++) {
      const childFlow = branch.nodes[nIdx];
      const stepNum = stepNumbers.get(childFlow.id) || 0;
      const emoji = TYPE_EMOJI[childFlow.step.type] || "\u2753";
      const typeLabelKey = TYPE_LABEL_KEY[childFlow.step.type];
      const typeLabel = typeLabelKey ? t(typeLabelKey) : childFlow.step.type;
      const hasOptions = (childFlow.step.type === "multiple_choice" || childFlow.step.type === "multi_select")
        && childFlow.step.config.options && childFlow.step.config.options.length > 0;
      const effectiveHeight = estimateNodeHeight(
        childFlow.step.config.label,
        Boolean(hasOptions),
        childFlow.step.config.options?.length ?? 0,
        Boolean(childFlow.step.config.goto),
      );

      const childGotoId = childFlow.step.config.goto;
      const childGotoInfo = childGotoId
        ? childGotoId === GOTO_END
          ? { kind: "end" as const }
          : { kind: "step" as const, targetNumber: stepIdToStepNumber.get(childGotoId) ?? 0 }
        : null;
      rfNodes.push({
        id: childFlow.id,
        type: "stepNode",
        position: { x: colCenterX - NODE_WIDTH / 2, y: colY },
        data: {
          flowNodeId: childFlow.id,
          emoji,
          label: childFlow.step.config.label,
          typeBadge: typeLabel,
          required: childFlow.step.required,
          options: hasOptions ? childFlow.step.config.options : null,
          stepNumber: stepNum,
          gotoInfo: childGotoInfo,
        },
        draggable: false,
      });

      rfEdges.push({
        id: `e-${prevId}-${childFlow.id}`,
        source: prevId,
        target: childFlow.id,
        type: "smoothstep",
        style: { stroke: "#6b7280", strokeWidth: 1.5 },
      });

      if (!firstNodeId) firstNodeId = childFlow.id;
      lastNodeId = childFlow.id;
      colY += effectiveHeight + 10;

      // Nested branches
      if (childFlow.branches && childFlow.branches.length > 0) {
        const nested = layoutBranches(
          childFlow,
          colCenterX,
          colY,
          stepNumbers,
          t,
          stepIdToFlowId,
          stepIdToStepNumber,
        );
        rfNodes.push(...nested.nodes);
        rfEdges.push(...nested.edges);
        colY += nested.height;
        lastNodeId = `__merge_${childFlow.id}__`;
      }

      // Add button after each branch step
      const addAfterId = `__add_branch_${parentFlowNode.id}_${bIdx}_${nIdx + 1}__`;
      rfNodes.push({
        id: addAfterId,
        type: "addButtonNode",
        position: { x: colCenterX - 15, y: colY },
        data: {
          insertTarget: "branch",
          parentNodeId: parentFlowNode.id,
          branchOption: branch.optionValue,
          insertIndex: nIdx + 1,
        },
        draggable: false,
        selectable: false,
      });

      const lastSource = childFlow.branches && childFlow.branches.length > 0
        ? `__merge_${childFlow.id}__`
        : childFlow.id;
      rfEdges.push({
        id: `e-${lastSource}-${addAfterId}`,
        source: lastSource,
        target: addAfterId,
        type: "smoothstep",
        style: { stroke: "#6b7280", strokeWidth: 1.5 },
      });

      colY += ADD_BUTTON_HEIGHT + 10;
      prevId = addAfterId;
      lastNodeId = addAfterId;
    }

    const branchHeight = colY - labelY;
    if (branchHeight > maxBranchHeight) maxBranchHeight = branchHeight;

    branchColumns.push({
      x: colCenterX,
      firstId: firstNodeId,
      lastId: lastNodeId,
      height: branchHeight,
      branchLabel: branch.optionValue,
    });
  }

  // Merge point node
  const mergeY = labelY + maxBranchHeight + 20;
  const mergeId = `__merge_${parentFlowNode.id}__`;
  rfNodes.push({
    id: mergeId,
    type: "mergeNode",
    position: { x: parentCenterX - 10, y: mergeY },
    data: {},
    draggable: false,
    selectable: false,
  });

  // Edges from each branch's last node to merge
  for (const col of branchColumns) {
    if (col.lastId) {
      rfEdges.push({
        id: `e-${col.lastId}-${mergeId}`,
        source: col.lastId,
        target: mergeId,
        type: "smoothstep",
        style: { stroke: "#6b7280", strokeWidth: 1.5, strokeDasharray: "5,5" },
      });
    }
  }

  const totalHeight = mergeY - startY + 40;
  return { nodes: rfNodes, edges: rfEdges, height: totalHeight };
}

// ============ Custom Node Components ============

// --- StartNode ---
const StartNodeComponent = memo(function StartNodeComponent(_props: NodeProps) {
  const { t } = useTranslation();
  return (
    <div className="w-[60px] h-[60px] rounded-full bg-green-500 flex items-center justify-center text-white text-[10px] font-bold shadow-lg border-2 border-green-600">
      <Handle type="source" position={Position.Bottom} className="!bg-green-700 !w-2 !h-2 !border-0" />
      {t("flow_editor.start")}
    </div>
  );
});

// --- EndNode ---
const EndNodeComponent = memo(function EndNodeComponent(_props: NodeProps) {
  const { t } = useTranslation();
  return (
    <div className="w-[60px] h-[60px] rounded-full bg-red-500 flex items-center justify-center text-white text-[10px] font-bold shadow-lg border-2 border-red-600">
      <Handle type="target" position={Position.Top} className="!bg-red-700 !w-2 !h-2 !border-0" />
      {t("flow_editor.end")}
    </div>
  );
});

// --- MergeNode (invisible merge point) ---
const MergeNodeComponent = memo(function MergeNodeComponent(_props: NodeProps) {
  return (
    <div className="w-5 h-5 rounded-full bg-gray-400 dark:bg-gray-500 border-2 border-gray-500 dark:border-gray-400 opacity-60">
      <Handle type="target" position={Position.Top} className="!bg-gray-500 !w-2 !h-2 !border-0" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-500 !w-2 !h-2 !border-0" />
    </div>
  );
});

// --- BranchLabelNode ---
const BranchLabelNodeComponent = memo(function BranchLabelNodeComponent({ data }: NodeProps) {
  return (
    <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-full border border-dashed border-gray-300 dark:border-gray-500 shadow-sm min-w-[80px] text-center">
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-2 !h-2 !border-0" />
      {data.label}
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400 !w-2 !h-2 !border-0" />
    </div>
  );
});

// --- AddButtonNode ---
const AddButtonNodeComponent = memo(function AddButtonNodeComponent({ data }: NodeProps) {
  return (
    <div className="flex items-center justify-center" data-add-target={JSON.stringify(data)}>
      <Handle type="target" position={Position.Top} className="!bg-transparent !w-0 !h-0 !border-0 !min-w-0 !min-h-0" />
      <button
        className="w-[30px] h-[30px] rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600
          text-gray-400 dark:text-gray-500 hover:border-green-500 hover:text-green-500 hover:bg-green-50
          dark:hover:bg-green-900/20 transition-all text-lg font-light flex items-center justify-center
          focus:outline-none focus:ring-2 focus:ring-green-400"
        data-add-button="true"
      >
        +
      </button>
      <Handle type="source" position={Position.Bottom} className="!bg-transparent !w-0 !h-0 !border-0 !min-w-0 !min-h-0" />
    </div>
  );
});

// --- StepNode ---
const StepNodeComponent = memo(function StepNodeComponent({ data, selected }: NodeProps) {
  const { t } = useTranslation();
  return (
    <div
      className={`px-4 py-3 rounded-xl border-2 bg-white dark:bg-gray-800 shadow-sm transition-all
        ${selected
          ? "border-blue-500 shadow-lg ring-2 ring-blue-300 dark:ring-blue-700"
          : "border-gray-200 dark:border-gray-700 hover:shadow-md"
        }`}
      style={{ width: NODE_WIDTH }}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-gray-800" />
      <div className="flex items-center gap-2.5">
        {/* Step number */}
        <div className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
          {data.stepNumber}
        </div>
        {/* Emoji */}
        <StepEmoji emoji={data.emoji} size={20} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-800 dark:text-gray-200 break-words whitespace-pre-line leading-snug">{data.label}</div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mt-0.5">{data.typeBadge}</div>
        </div>
        {data.required && (
          <span className="text-[10px] font-bold text-red-500 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded">{t("flow_editor.required_badge")}</span>
        )}
      </div>
      {data.options && data.options.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {data.options.map((opt: OnboardingOption, idx: number) => {
            const text = optionText(opt);
            return (
              <span key={`${idx}-${text}`} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300">{text}</span>
            );
          })}
        </div>
      )}
      {data.gotoInfo && (
        <div className="mt-2 inline-flex items-center gap-1 rounded bg-orange-100 dark:bg-orange-900/30 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700 dark:text-orange-300">
          {data.gotoInfo.kind === "end"
            ? t("flow_editor.goto_badge_end")
            : t("flow_editor.goto_badge", { n: data.gotoInfo.targetNumber })}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400 !w-2.5 !h-2.5 !border-2 !border-white dark:!border-gray-800" />
    </div>
  );
});

// ============ Node types registry ============

const nodeTypes = {
  startNode: StartNodeComponent,
  endNode: EndNodeComponent,
  mergeNode: MergeNodeComponent,
  branchLabelNode: BranchLabelNodeComponent,
  addButtonNode: AddButtonNodeComponent,
  stepNode: StepNodeComponent,
};

// ============ Properties Panel ============

function PropertiesPanel({
  step,
  nodeId,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onClose,
  gotoTargets,
}: {
  step: OnboardingStep;
  nodeId: string;
  onUpdate: (nodeId: string, updater: (step: OnboardingStep) => OnboardingStep) => void;
  onDelete: (nodeId: string) => void;
  onMoveUp: (nodeId: string) => void;
  onMoveDown: (nodeId: string) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onClose: () => void;
  /** Lista de steps elegíveis como destino do goto deste step. */
  gotoTargets: Array<{ id: string; label: string; stepNumber: number }>;
}) {
  const { t } = useTranslation();
  const updateConfig = (partial: Partial<OnboardingStepConfig>) => {
    onUpdate(nodeId, (s) => ({ ...s, config: { ...s.config, ...partial } }));
  };

  const emoji = TYPE_EMOJI[step.type] || "\u2753";
  const typeLabelKey = TYPE_LABEL_KEY[step.type];
  const typeLabel = typeLabelKey ? t(typeLabelKey) : step.type;

  return (
    <div className="w-72 border-l border-[var(--border)] bg-[var(--card)] overflow-y-auto shrink-0 flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-[var(--border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{emoji}</span>
          <div>
            <h3 className="text-sm font-semibold text-[var(--foreground)]">{t("flow_editor.properties")}</h3>
            <p className="text-[10px] text-[var(--muted-foreground)] uppercase tracking-wider">{typeLabel}</p>
          </div>
        </div>
        <button
          className="w-6 h-6 rounded flex items-center justify-center text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--accent)] transition-colors"
          onClick={onClose}
          title="Fechar"
        >
          &#10005;
        </button>
      </div>

      {/* Body */}
      <div className="p-3 space-y-3 flex-1 text-sm" onClick={(e) => e.stopPropagation()}>
        {/* Label */}
        <FieldRow label={t("flow_editor.displayed_text")}>
          <textarea
            rows={3}
            className="w-full resize-y rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm leading-snug text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
            value={step.config.label}
            onChange={(e) => updateConfig({ label: e.target.value })}
          />
        </FieldRow>

        {/* Required toggle */}
        <FieldRow label={t("flow_editor.required")}>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={step.required}
              onChange={(e) => onUpdate(nodeId, (s) => ({ ...s, required: e.target.checked }))}
            />
            <div className="w-10 h-6 bg-gray-300 dark:bg-gray-600 border border-gray-400 dark:border-gray-500 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:shadow-sm after:transition-all peer-checked:bg-green-500 peer-checked:border-green-600" />
          </label>
        </FieldRow>

        {/* Goto: jump to specific step after completion */}
        <FieldRow label={t("flow_editor.goto_label")}>
          <select
            className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
            value={step.config.goto ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              updateConfig({ goto: v === "" ? undefined : v });
            }}
          >
            <option value="">{t("flow_editor.goto_next_default")}</option>
            <option value={GOTO_END}>{t("flow_editor.goto_end")}</option>
            {gotoTargets.length > 0 && (
              <optgroup label={t("flow_editor.goto_other_steps")}>
                {gotoTargets.map((tg) => (
                  <option key={tg.id} value={tg.id}>
                    {tg.stepNumber}. {tg.label.length > 32 ? tg.label.slice(0, 32) + "…" : tg.label}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </FieldRow>

        {/* Type-specific fields */}
        {step.type === "text_input" && (
          <FieldRow label={t("flow_editor.placeholder_label")}>
            <input
              type="text"
              className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
              value={step.config.placeholder || ""}
              onChange={(e) => updateConfig({ placeholder: e.target.value })}
            />
          </FieldRow>
        )}

        {(step.type === "multiple_choice" || step.type === "multi_select") && (
          <OptionsEditor
            options={step.config.options || []}
            onChange={(options) => updateConfig({ options })}
          />
        )}

        {step.type === "conditional_input" && (
          <ConditionalInputEditor
            value={step.config.inputOptions || []}
            onChange={(inputOptions) => updateConfig({ inputOptions })}
          />
        )}

        {step.type === "document_upload" && (
          /*
           * Tipos aceitos (image/* + pdf) e tamanho maximo (10 MB) ficam fixos
           * no default da paleta — gestor nao precisa nem deveria escolher
           * (evita digitar valor absurdo tipo 500 MB). Aqui ele so configura
           * quantos arquivos coletar + nome de cada (ex: "Frente" + "Verso").
           */
          <DocumentFieldsEditor
            fields={step.config.fields || []}
            onChange={(fields) => updateConfig({ fields })}
          />
        )}

        {/*
         * Selfie e video_recording nao expoem mais o seletor de camera no
         * admin — confundia o gestor. O cliente escolhe na hora da captura
         * pelo proprio app (ImagePicker nativo permite alternar). Default
         * da paleta: selfie=front, video=back, mas o usuario pode trocar.
         */}

        {step.type === "create_password" && (
          <FieldRow label={t("flow_editor.min_chars")}>
            <input
              type="number"
              className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
              value={step.config.minLength ?? ""}
              onChange={(e) =>
                updateConfig({ minLength: e.target.value ? Number(e.target.value) : undefined })
              }
              min={1}
            />
          </FieldRow>
        )}

        {step.type === "info_screen" && (
          <>
            <FieldRow label={t("flow_editor.message")}>
              <textarea
                className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)] resize-y min-h-[60px]"
                value={step.config.message || ""}
                onChange={(e) => updateConfig({ message: e.target.value })}
                rows={3}
              />
            </FieldRow>

            <FieldRow label={t("flow_editor.require_accept")}>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={Boolean(step.config.requireAccept)}
                  onChange={(e) =>
                    updateConfig({
                      requireAccept: e.target.checked,
                      acceptText:
                        e.target.checked && !step.config.acceptText
                          ? t("flow_editor.accept_text_default")
                          : step.config.acceptText,
                    })
                  }
                />
                <div className="w-10 h-6 bg-gray-300 dark:bg-gray-600 border border-gray-400 dark:border-gray-500 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:shadow-sm after:transition-all peer-checked:bg-green-500 peer-checked:border-green-600" />
              </label>
            </FieldRow>

            {step.config.requireAccept && (
              <FieldRow label={t("flow_editor.accept_text_label")}>
                <textarea
                  className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)] resize-y min-h-[48px]"
                  value={step.config.acceptText ?? ""}
                  onChange={(e) => updateConfig({ acceptText: e.target.value })}
                  placeholder={t("flow_editor.accept_text_default")}
                  rows={2}
                />
              </FieldRow>
            )}
          </>
        )}

        {(step.type === "currency_input" || step.type === "number_input") && (
          <div className="flex gap-2">
            <FieldRow label={t("flow_editor.min")}>
              <input
                type="number"
                className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
                value={step.config.min ?? ""}
                onChange={(e) =>
                  updateConfig({ min: e.target.value ? Number(e.target.value) : undefined })
                }
              />
            </FieldRow>
            <FieldRow label={t("flow_editor.max")}>
              <input
                type="number"
                className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
                value={step.config.max ?? ""}
                onChange={(e) =>
                  updateConfig({ max: e.target.value ? Number(e.target.value) : undefined })
                }
              />
            </FieldRow>
          </div>
        )}

        {step.type === "date_input" && (
          <FieldRow label={t("flow_editor.placeholder_label")}>
            <input
              type="text"
              className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
              value={step.config.placeholder || ""}
              onChange={(e) => updateConfig({ placeholder: e.target.value })}
              placeholder="DD/MM/AAAA"
            />
          </FieldRow>
        )}

        {step.type === "references" && (
          /*
           * Quantidade fica entre 1 e 5 — mais que 5 vira atrito (cliente
           * desiste). Mobile le `config.quantity ?? 2` e renderiza N cards.
           */
          <FieldRow
            label={t("flow_editor.refs_quantity_label")}
            hint={t("flow_editor.refs_quantity_hint")}
          >
            <select
              className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1.5 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
              value={step.config.quantity ?? 2}
              onChange={(e) => updateConfig({ quantity: Number(e.target.value) })}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </FieldRow>
        )}
      </div>

      {/* Footer actions */}
      <div className="p-3 border-t border-[var(--border)] space-y-2">
        {/* Move buttons */}
        <div className="flex gap-2">
          <button
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-[var(--border)] text-xs text-[var(--foreground)] hover:bg-[var(--accent)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={() => onMoveUp(nodeId)}
            disabled={!canMoveUp}
            title="Mover para cima"
          >
            &#9650; {t("flow_editor.move_up")}
          </button>
          <button
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-[var(--border)] text-xs text-[var(--foreground)] hover:bg-[var(--accent)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={() => onMoveDown(nodeId)}
            disabled={!canMoveDown}
            title="Mover para baixo"
          >
            &#9660; {t("flow_editor.move_down")}
          </button>
        </div>
        {/* Delete */}
        <button
          className="w-full px-2 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors"
          onClick={() => onDelete(nodeId)}
        >
          &#10005; {t("flow_editor.remove_step")}
        </button>
      </div>
    </div>
  );
}

// ============ FieldRow ============

function FieldRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-[var(--muted-foreground)] mb-0.5">
        {label}
      </label>
      {children}
      {hint && (
        <p className="mt-1 text-[10px] leading-snug text-[var(--muted-foreground)]">
          {hint}
        </p>
      )}
    </div>
  );
}

// ============ OptionsEditor ============

function OptionsEditor({
  options,
  onChange,
}: {
  options: OnboardingOption[];
  onChange: (options: string[]) => void;
}) {
  const { t } = useTranslation();
  const addOption = () => {
    onChange([
      ...options.map(optionText),
      t("flow_editor.auto_option", { num: options.length + 1 }),
    ]);
  };

  const removeOption = (index: number) => {
    if (options.length <= 1) return;
    onChange(options.filter((_, i) => i !== index).map(optionText));
  };

  const updateOption = (index: number, value: string) => {
    onChange(options.map((o, i) => (i === index ? value : optionText(o))));
  };

  return (
    <div>
      <label className="block text-[11px] font-medium text-[var(--muted-foreground)] mb-1">
        {t("flow_editor.options")}
      </label>
      <div className="space-y-1">
        {options.map((opt, i) => (
          <div key={i} className="flex items-center gap-1">
            <input
              type="text"
              className="flex-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
              value={optionText(opt)}
              onChange={(e) => updateOption(i, e.target.value)}
            />
            {options.length > 1 && (
              <button
                className="w-5 h-5 rounded flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs"
                onClick={() => removeOption(i)}
                title={t("flow_editor.remove_option")}
              >
                &#10005;
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        className="mt-1 text-xs text-[var(--primary-600,#5B21B6)] hover:underline"
        onClick={addOption}
      >
        {t("flow_editor.add_option")}
      </button>
    </div>
  );
}

// ============ ConditionalInputEditor ============

/**
 * Editor das opcoes de um `conditional_input`. Cada item da lista tem:
 *   - label (o que aparece no radio: "E-mail", "Telefone", ...)
 *   - input.type (text/email/phone/cpf/number/currency/date — define mascara)
 *   - input.label (rotulo acima do input que abre: "E-mail vinculado")
 *   - input.placeholder
 * O `value` interno e derivado do label (slugificado), nao editavel diretamente.
 */
function ConditionalInputEditor({
  value,
  onChange,
}: {
  value: ConditionalInputOption[];
  onChange: (value: ConditionalInputOption[]) => void;
}) {
  const { t } = useTranslation();

  const slugify = (s: string): string =>
    s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "opcao";

  const inputTypes: ConditionalInputType[] = [
    "text", "email", "phone", "cpf", "number", "currency", "date",
  ];

  const placeholderFor = (type: ConditionalInputType): string => {
    switch (type) {
      case "email": return "exemplo@email.com";
      case "phone": return "(00) 00000-0000";
      case "cpf": return "000.000.000-00";
      case "number": return "0";
      case "currency": return "R$ 0,00";
      case "date": return "DD/MM/AAAA";
      default: return "Digite aqui...";
    }
  };

  const addOption = () => {
    const next: ConditionalInputOption = {
      value: `opcao${value.length + 1}`,
      label: t("flow_editor.conditional.new_option_label", {
        num: value.length + 1,
        defaultValue: `Opcao ${value.length + 1}`,
      }),
      input: { type: "text", label: "", placeholder: placeholderFor("text") },
    };
    onChange([...value, next]);
  };

  const removeOption = (index: number) => {
    if (value.length <= 2) return; // minimo 2 opcoes
    onChange(value.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, partial: Partial<ConditionalInputOption>) => {
    onChange(value.map((o, i) => {
      if (i !== index) return o;
      const merged = { ...o, ...partial };
      if (partial.label !== undefined) merged.value = slugify(partial.label);
      return merged;
    }));
  };

  const updateInput = (index: number, partial: Partial<ConditionalInputOption["input"]>) => {
    onChange(value.map((o, i) => {
      if (i !== index) return o;
      const merged = { ...o, input: { ...o.input, ...partial } };
      // Quando troca o tipo, sugere placeholder novo se o campo estiver vazio
      // ou ainda igual ao placeholder do tipo anterior.
      if (partial.type) {
        const oldPh = placeholderFor(o.input.type);
        if (!o.input.placeholder || o.input.placeholder === oldPh) {
          merged.input.placeholder = placeholderFor(partial.type);
        }
      }
      return merged;
    }));
  };

  return (
    <div>
      <label className="block text-[11px] font-medium text-[var(--muted-foreground)] mb-1">
        {t("flow_editor.conditional.options")}
      </label>
      <div className="space-y-2">
        {value.map((opt, i) => (
          <div
            key={i}
            className="rounded-md border border-[var(--border)] bg-[var(--background)] p-2 space-y-1.5"
          >
            <div className="flex items-center gap-1">
              <input
                type="text"
                className="flex-1 rounded border border-[var(--border)] bg-[var(--card)] px-2 py-1 text-[12px] text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
                value={opt.label}
                onChange={(e) => updateOption(i, { label: e.target.value })}
                placeholder={t("flow_editor.conditional.option_label_ph")}
              />
              {value.length > 2 && (
                <button
                  className="w-5 h-5 rounded flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs"
                  onClick={() => removeOption(i)}
                  title={t("flow_editor.remove_option")}
                >
                  &#10005;
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-1">
              <select
                className="rounded border border-[var(--border)] bg-[var(--card)] px-1.5 py-1 text-[11px] text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
                value={opt.input.type}
                onChange={(e) => updateInput(i, { type: e.target.value as ConditionalInputType })}
              >
                {inputTypes.map((tp) => (
                  <option key={tp} value={tp}>
                    {t(`flow_editor.conditional.input_type.${tp}`, { defaultValue: tp })}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="rounded border border-[var(--border)] bg-[var(--card)] px-1.5 py-1 text-[11px] text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
                value={opt.input.placeholder || ""}
                onChange={(e) => updateInput(i, { placeholder: e.target.value })}
                placeholder={placeholderFor(opt.input.type)}
              />
            </div>
            <input
              type="text"
              className="w-full rounded border border-[var(--border)] bg-[var(--card)] px-2 py-1 text-[11px] text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
              value={opt.input.label || ""}
              onChange={(e) => updateInput(i, { label: e.target.value })}
              placeholder={t("flow_editor.conditional.input_label_ph")}
            />
          </div>
        ))}
      </div>
      <button
        className="mt-2 text-xs text-[var(--primary-600,#5B21B6)] hover:underline"
        onClick={addOption}
      >
        {t("flow_editor.conditional.add_option")}
      </button>
    </div>
  );
}

// ============ DocumentFieldsEditor ============

/**
 * Editor da lista de arquivos que um step `document_upload` coleta.
 * Caso classico: 1 step "CNH" com 2 fields (frente + verso). Cada field
 * vira um slot labelado no mobile (DocumentUploadStep).
 *
 * `name` (id interno) e derivado automaticamente do label (slugificado)
 * pra evitar o gestor lidar com identificadores tecnicos.
 */
function DocumentFieldsEditor({
  fields,
  onChange,
}: {
  fields: DocumentFieldConfig[];
  onChange: (fields: DocumentFieldConfig[]) => void;
}) {
  const { t } = useTranslation();

  const slugify = (s: string): string => {
    const base = s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    return base || "doc";
  };

  // Garante nomes unicos (caso o gestor de o mesmo label pra 2 arquivos).
  const uniqueName = (base: string, existing: Set<string>): string => {
    if (!existing.has(base)) return base;
    let i = 2;
    while (existing.has(`${base}_${i}`)) i++;
    return `${base}_${i}`;
  };

  const addField = () => {
    const existing = new Set(fields.map((f) => f.name));
    const baseLabel = t("flow_editor.docs.new_label", {
      num: fields.length + 1,
      defaultValue: `Documento ${fields.length + 1}`,
    });
    onChange([
      ...fields,
      {
        name: uniqueName(slugify(baseLabel), existing),
        label: baseLabel,
        required: true,
      },
    ]);
  };

  const removeField = (index: number) => {
    if (fields.length <= 1) return;
    onChange(fields.filter((_, i) => i !== index));
  };

  const updateField = (index: number, partial: Partial<DocumentFieldConfig>) => {
    onChange(fields.map((f, i) => {
      if (i !== index) return f;
      const merged = { ...f, ...partial };
      if (partial.label !== undefined) {
        const others = new Set(fields.filter((_, j) => j !== index).map((x) => x.name));
        merged.name = uniqueName(slugify(partial.label), others);
      }
      return merged;
    }));
  };

  return (
    <div>
      <label className="block text-[11px] font-medium text-[var(--muted-foreground)] mb-1">
        {t("flow_editor.docs.fields_label")}
      </label>
      <p className="text-[10px] text-[var(--muted-foreground)] mb-2 leading-snug">
        {t("flow_editor.docs.fields_hint")}
      </p>
      <div className="space-y-1.5">
        {fields.map((f, i) => (
          <div key={i} className="flex items-center gap-1">
            <input
              type="text"
              className="flex-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-sm text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--primary-500,#6D49FF)]"
              value={f.label}
              onChange={(e) => updateField(i, { label: e.target.value })}
              placeholder={t("flow_editor.docs.label_ph")}
            />
            <label
              className="flex items-center gap-1 text-[10px] text-[var(--muted-foreground)]"
              title={t("flow_editor.docs.required_hint")}
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={f.required !== false}
                onChange={(e) => updateField(i, { required: e.target.checked })}
              />
              {t("flow_editor.docs.required_short")}
            </label>
            {fields.length > 1 && (
              <button
                className="w-5 h-5 rounded flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs"
                onClick={() => removeField(i)}
                title={t("flow_editor.remove_option")}
              >
                &#10005;
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        className="mt-2 text-xs text-[var(--primary-600,#5B21B6)] hover:underline"
        onClick={addField}
      >
        {t("flow_editor.docs.add_field")}
      </button>
    </div>
  );
}

// ============ Sidebar ============

function Sidebar({
  onAddToRoot,
}: {
  onAddToRoot: (item: PaletteItem) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="w-56 border-r border-[var(--border)] bg-[var(--card)] overflow-y-auto shrink-0">
      <div className="p-3 border-b border-[var(--border)]">
        <h3 className="text-sm font-semibold text-[var(--foreground)]">{t("flow_editor.components")}</h3>
        <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{t("flow_editor.components_hint")}</p>
      </div>
      <div className="p-2">
        {CATEGORY_KEYS.map((catKey) => {
          const items = PALETTE_ITEMS.filter((i) => i.categoryKey === catKey);
          if (items.length === 0) return null;
          return (
            <div key={catKey} className="mb-3">
              {/*
               * Categoria em formato de badge (pill verde) — separa visual-
               * mente os grupos de itens da paleta em vez do label sozinho.
               */}
              <div className="px-2 mb-1.5 mt-1">
                <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-800">
                  {t(catKey)}
                </span>
              </div>
              {items.map((item, idx) => (
                <button
                  key={`${item.type}-${idx}`}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm text-[var(--foreground)] hover:bg-[var(--accent)] transition-colors"
                  onClick={() => onAddToRoot(item)}
                  title={t(item.labelKey)}
                >
                  <StepEmoji emoji={item.emoji} size={16} />
                  <span className="truncate">{t(item.labelKey)}</span>
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Add Step Popover (for "+" buttons) ============

function AddStepPopover({
  position,
  onSelect,
  onClose,
}: {
  position: { x: number; y: number };
  onSelect: (item: PaletteItem) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as HTMLElement)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  // Adjust vertical position: if too close to bottom, flip up
  const [adjustedY, setAdjustedY] = useState(position.y);
  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      if (rect.bottom > window.innerHeight - 10) {
        setAdjustedY(position.y - rect.height - 10);
      }
    }
  }, [position.y]);

  return (
    <>
      {/* Backdrop semi-transparente para destacar o popover do fluxo */}
      <div
        className="fixed inset-0 z-[99] bg-black/30 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div
        ref={ref}
        className="fixed z-[100] w-64 max-h-[420px] flex flex-col bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl ring-1 ring-black/5"
        style={{ left: position.x, top: adjustedY }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-xl">
          <div className="flex items-center gap-2">
            <span className="text-base">+</span>
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{t("flow_editor.add_component")}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Items */}
        <div className="p-1.5 overflow-y-auto flex-1">
          {CATEGORY_KEYS.map((catKey) => {
            const items = PALETTE_ITEMS.filter((i) => i.categoryKey === catKey);
            if (items.length === 0) return null;
            return (
              <div key={catKey}>
                <div className="px-2 pt-2 pb-1">
                  <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-800">
                    {t(catKey)}
                  </span>
                </div>
                {items.map((item, idx) => (
                  <button
                    key={`${item.type}-${idx}`}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => {
                      onSelect(item);
                      onClose();
                    }}
                  >
                    <StepEmoji emoji={item.emoji} size={16} />
                    <span className="truncate">{t(item.labelKey)}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ============ Helper: find FlowNode by id and compute move info ============

function findFlowNodeById(nodes: FlowNode[], id: string): FlowNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.branches) {
      for (const b of n.branches) {
        const found = findFlowNodeById(b.nodes, id);
        if (found) return found;
      }
    }
  }
  return null;
}

function findNodeContext(
  nodes: FlowNode[],
  id: string,
): { index: number; siblings: FlowNode[] } | null {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === id) return { index: i, siblings: nodes };
    if (nodes[i].branches) {
      for (const b of nodes[i].branches!) {
        const found = findNodeContext(b.nodes, id);
        if (found) return found;
      }
    }
  }
  return null;
}

// ============ Main Component ============

export default function FlowEditor({
  steps,
  onChange,
  simulatorPlans,
  simulatorValorMinimo,
}: FlowEditorProps) {
  const { t } = useTranslation();
  const [flowNodes, setFlowNodes] = useState<FlowNode[]>(() => parseFlowTree(steps));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addPopover, setAddPopover] = useState<{
    position: { x: number; y: number };
    insertTarget: "root" | "branch";
    insertIndex: number;
    parentNodeId?: string;
    branchOption?: string;
  } | null>(null);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);

  // Track if the last change came from inside this component
  // (to avoid re-parsing and losing node IDs / selection)
  const internalUpdateRef = useRef(false);

  // Sync external steps changes (skip if we just emitted them ourselves)
  useEffect(() => {
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return;
    }
    setFlowNodes(parseFlowTree(steps));
  }, [steps]);

  // Emit changes
  const emitChange = useCallback(
    (updatedNodes: FlowNode[]) => {
      setFlowNodes(updatedNodes);
      internalUpdateRef.current = true;
      onChange(flattenFlowTree(updatedNodes));
    },
    [onChange],
  );

  // Step numbering
  const stepNumbers = useMemo(() => computeStepNumbers(flowNodes), [flowNodes]);

  // Compute ReactFlow layout
  const { rfNodes, rfEdges } = useMemo(
    () => autoLayout(flowNodes, stepNumbers, t),
    [flowNodes, stepNumbers, t],
  );

  // ---- Node operations ----

  const addNodeToRoot = useCallback(
    (item: PaletteItem, insertIndex?: number) => {
      const fields = collectFields(flowNodes);
      const step = createStepFromPalette(item, fields);
      const node: FlowNode = { id: uid(), step };

      if (step.type === "multiple_choice" && step.config.options) {
        node.branches = step.config.options.map((opt) => ({
          optionValue: optionText(opt),
          nodes: [],
        }));
      }

      const updated = [...flowNodes];
      const idx = insertIndex !== undefined ? insertIndex : updated.length;
      updated.splice(idx, 0, node);
      emitChange(updated);
      setSelectedId(node.id);
    },
    [flowNodes, emitChange],
  );

  const addNodeToBranch = useCallback(
    (parentNodeId: string, branchOption: string, item: PaletteItem, insertIndex?: number) => {
      const fields = collectFields(flowNodes);
      const step = createStepFromPalette(item, fields);
      const newNode: FlowNode = { id: uid(), step };

      if (step.type === "multiple_choice" && step.config.options) {
        newNode.branches = step.config.options.map((opt) => ({
          optionValue: optionText(opt),
          nodes: [],
        }));
      }

      function insertInBranch(list: FlowNode[]): FlowNode[] {
        return list.map((n) => {
          if (n.id === parentNodeId && n.branches) {
            return {
              ...n,
              branches: n.branches.map((b) => {
                if (b.optionValue === branchOption) {
                  const newNodes = [...b.nodes];
                  const idx = insertIndex !== undefined ? insertIndex : newNodes.length;
                  newNodes.splice(idx, 0, newNode);
                  return { ...b, nodes: newNodes };
                }
                return b;
              }),
            };
          }
          if (n.branches) {
            return {
              ...n,
              branches: n.branches.map((b) => ({
                ...b,
                nodes: insertInBranch(b.nodes),
              })),
            };
          }
          return n;
        });
      }

      const updated = insertInBranch(flowNodes);
      emitChange(updated);
      setSelectedId(newNode.id);
    },
    [flowNodes, emitChange],
  );

  const deleteNode = useCallback(
    (nodeId: string) => {
      function removeFromList(list: FlowNode[]): FlowNode[] {
        return list
          .filter((n) => n.id !== nodeId)
          .map((n) => {
            if (n.branches) {
              return {
                ...n,
                branches: n.branches.map((b) => ({
                  ...b,
                  nodes: removeFromList(b.nodes),
                })),
              };
            }
            return n;
          });
      }

      const updated = removeFromList(flowNodes);
      emitChange(updated);
      if (selectedId === nodeId) setSelectedId(null);
    },
    [flowNodes, emitChange, selectedId],
  );

  const moveNode = useCallback(
    (nodeId: string, direction: "up" | "down") => {
      function moveInList(list: FlowNode[]): FlowNode[] {
        const idx = list.findIndex((n) => n.id === nodeId);
        if (idx !== -1) {
          const newIdx = direction === "up" ? idx - 1 : idx + 1;
          if (newIdx < 0 || newIdx >= list.length) return list;
          const copy = [...list];
          [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
          return copy;
        }
        return list.map((n) => {
          if (n.branches) {
            return {
              ...n,
              branches: n.branches.map((b) => ({
                ...b,
                nodes: moveInList(b.nodes),
              })),
            };
          }
          return n;
        });
      }

      emitChange(moveInList(flowNodes));
    },
    [flowNodes, emitChange],
  );

  const updateNodeStep = useCallback(
    (nodeId: string, updater: (step: OnboardingStep) => OnboardingStep) => {
      function updateInList(list: FlowNode[]): FlowNode[] {
        return list.map((n) => {
          if (n.id === nodeId) {
            const newStep = updater(n.step);
            const newNode: FlowNode = { ...n, step: newStep };

            if (newStep.type === "multiple_choice" && newStep.config.options) {
              const existingBranches = n.branches || [];
              newNode.branches = newStep.config.options.map((opt) => {
                const value = optionText(opt);
                const existing = existingBranches.find((b) => b.optionValue === value);
                return existing || { optionValue: value, nodes: [] };
              });
            } else if (newStep.type !== "multiple_choice") {
              delete newNode.branches;
            }

            return newNode;
          }
          if (n.branches) {
            return {
              ...n,
              branches: n.branches.map((b) => ({
                ...b,
                nodes: updateInList(b.nodes),
              })),
            };
          }
          return n;
        });
      }

      emitChange(updateInList(flowNodes));
    },
    [flowNodes, emitChange],
  );

  // ---- ReactFlow event handlers ----

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (node.type === "stepNode") {
        setSelectedId(node.data.flowNodeId);
        setAddPopover(null);
      } else if (node.type === "addButtonNode") {
        // Open add popover near the clicked button
        const target = _event.target as HTMLElement;
        const button = target.closest("[data-add-button]") || target;
        const rect = button.getBoundingClientRect();

        const data = node.data;
        setAddPopover({
          position: { x: rect.right + 8, y: rect.top - 10 },
          insertTarget: data.insertTarget,
          insertIndex: data.insertIndex,
          parentNodeId: data.parentNodeId,
          branchOption: data.branchOption,
        });
        setSelectedId(null);
      } else {
        setSelectedId(null);
        setAddPopover(null);
      }
    },
    [],
  );

  const onPaneClick = useCallback(() => {
    setSelectedId(null);
    setAddPopover(null);
  }, []);

  // Handle add from popover
  const handleAddFromPopover = useCallback(
    (item: PaletteItem) => {
      if (!addPopover) return;
      if (addPopover.insertTarget === "root") {
        addNodeToRoot(item, addPopover.insertIndex);
      } else if (addPopover.insertTarget === "branch" && addPopover.parentNodeId && addPopover.branchOption !== undefined) {
        addNodeToBranch(addPopover.parentNodeId, addPopover.branchOption, item, addPopover.insertIndex);
      }
      setAddPopover(null);
    },
    [addPopover, addNodeToRoot, addNodeToBranch],
  );

  // Handle keyboard delete
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Delete" && selectedId) {
        // Don't delete if an input/textarea is focused
        const tag = (e.target as HTMLElement).tagName.toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select") return;
        deleteNode(selectedId);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedId, deleteNode]);

  // Fit view when layout changes
  useEffect(() => {
    if (rfInstance) {
      // Small delay to allow nodes to render
      const timeout = setTimeout(() => {
        rfInstance.fitView({ padding: 0.2, duration: 300 });
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [rfNodes.length, rfInstance]);

  // ---- Selected node info for properties panel ----
  const selectedFlowNode = selectedId ? findFlowNodeById(flowNodes, selectedId) : null;
  const selectedContext = selectedId ? findNodeContext(flowNodes, selectedId) : null;

  // Coleta todos os steps elegíveis como destino de goto (todos exceto o
  // próprio step selecionado), em ordem de execução. Usado pelo dropdown
  // "Ir para" no painel de propriedades.
  const gotoTargets = useMemo(() => {
    if (!selectedFlowNode?.step.id) return [];
    const flat = flattenFlowTree(flowNodes);
    const numbersByOrder = new Map<number, number>();
    flat.forEach((s, i) => numbersByOrder.set(s.order, i + 1));
    return flat
      .filter((s) => s.id && s.id !== selectedFlowNode.step.id)
      .map((s) => ({
        id: s.id!,
        label: s.config.label || s.type,
        stepNumber: numbersByOrder.get(s.order) ?? 0,
      }));
  }, [flowNodes, selectedFlowNode?.step.id]);

  return (
    <div className="flex h-full rounded-xl border border-[var(--border)] bg-[var(--card)] overflow-hidden">
      {/* Sidebar */}
      <Sidebar onAddToRoot={(item) => addNodeToRoot(item)} />

      {/* ReactFlow Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={rfNodes.map((n) => ({
            ...n,
            selected: n.type === "stepNode" && n.data.flowNodeId === selectedId,
          }))}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onInit={setRfInstance}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          proOptions={{ hideAttribution: true }}
        >
          <Controls
            position="bottom-left"
            showInteractive={false}
            className="!bg-[var(--card)] !border-[var(--border)] !shadow-lg [&>button]:!bg-[var(--card)] [&>button]:!border-[var(--border)] [&>button]:!text-[var(--foreground)] [&>button:hover]:!bg-[var(--accent)]"
          />
          <MiniMap
            position="bottom-right"
            nodeColor={(node) => {
              if (node.type === "startNode") return "#22c55e";
              if (node.type === "endNode") return "#ef4444";
              if (node.type === "stepNode") return node.selected ? "#3b82f6" : "#e5e7eb";
              if (node.type === "branchLabelNode") return "#d1d5db";
              return "transparent";
            }}
            maskColor="rgba(0,0,0,0.08)"
            className="!bg-[var(--card)] !border-[var(--border)]"
          />
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#d1d5db" className="dark:!opacity-20" />
        </ReactFlow>

        {/* Step count badge */}
        <div className="absolute top-3 left-3 z-10 px-3 py-1.5 bg-[var(--card)] border border-[var(--border)] rounded-lg shadow-sm text-xs text-[var(--muted-foreground)]">
          {t("flow_editor.step_count", { count: flowNodes.length })}
        </div>
      </div>

      {/* Live mobile preview */}
      <FlowPreview
        steps={flattenFlowTree(flowNodes)}
        selectedStepId={selectedFlowNode?.step.id ?? null}
        simulatorPlans={simulatorPlans}
        simulatorValorMinimo={simulatorValorMinimo}
      />

      {/* Properties Panel */}
      {selectedFlowNode && selectedContext && (
        <PropertiesPanel
          key={selectedId}
          step={selectedFlowNode.step}
          nodeId={selectedFlowNode.id}
          onUpdate={updateNodeStep}
          onDelete={(id) => {
            deleteNode(id);
            setSelectedId(null);
          }}
          onMoveUp={(id) => moveNode(id, "up")}
          onMoveDown={(id) => moveNode(id, "down")}
          canMoveUp={selectedContext.index > 0}
          canMoveDown={selectedContext.index < selectedContext.siblings.length - 1}
          onClose={() => setSelectedId(null)}
          gotoTargets={gotoTargets}
        />
      )}

      {/* Add Step Popover */}
      {addPopover && (
        <AddStepPopover
          position={addPopover.position}
          onSelect={handleAddFromPopover}
          onClose={() => setAddPopover(null)}
        />
      )}
    </div>
  );
}
